import { Tag } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/Pages/Test/Types/TestQuestion';
import { ContentType } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/Types/CourseContent.types';
import { GroupPermissions } from '@Pages/Roles/Types/Roles.types';
import { Course, CourseUnit } from '@Services/classrooms/classrooms.res.types';

export type DropdownsState = {
	dropdownInstructors: Array<DropdownInstructor>;
	dropdownCategories: Array<DropdownCategory>;
	dropdownClassrooms: Array<DropdownClassroom>;
	dropdownClassroomsByInstructor: Array<DropdownClassroom>;
	dropdownCourses: Array<DropdownCourse>;
	dropdownBatches: Array<DropdownBatch>;
	dropdownWalletBatches: Array<DropdownBatch>;
	dropdownTags: Array<Tag>;
	group_permissions: Array<GroupPermissions>;
	dropdownUnits: Array<DropdownUnit>;
	dropdownTestAttempts: Array<DropDownTestAttempts>;
	dropdownStandaloneCourses: Array<DropdownProduct>;
	dropdownBundles: Array<DropdownProduct>;
};

/**
 * Standalone courses and bundles share a shape here: both are top-level,
 * instructor-owned, priced products that a scratch card or promocode can target.
 */
export type DropdownProduct = {
	id: number;
	title: string;
	label: string;
	code: string;
	price: number;
	instructor_id: number;
	instructor?: {
		label?: string;
		user?: { first_name?: string; last_name?: string };
	};
};

/**
 * "Title (label) - First Last". Titles alone are ambiguous once several
 * instructors publish similarly named products, and the label is the handle
 * staff actually search by, so both belong in the option.
 *
 * The label is bracketed rather than dash-separated because labels are
 * themselves hyphenated slugs — "Physics - ali-physics-3rd-sec (Ali)" gives no
 * clue which dash separates what. Brackets bound the slug, leaving a single
 * dash to mean "…by this instructor".
 *
 * Every part degrades independently: no label gives "Title - First Last", no
 * instructor gives "Title (label)", and the instructor's own label stands in
 * when the user record carries no name.
 */
export const productOptionLabel = (product: {
	title: string;
	label?: string;
	instructor?: DropdownProduct['instructor'];
}): string => {
	const user = product.instructor?.user;
	const name =
		`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() ||
		product.instructor?.label;
	const titleWithLabel = product.label
		? `${product.title} (${product.label})`
		: product.title;
	return name ? `${titleWithLabel} - ${name}` : titleWithLabel;
};

export type DropdownInstructor = {
	id: number;
	first_name: string;
	last_name: string;
};

export type DropdownCategory = {
	id: number;
	name: string;
};

export type DropdownClassroom = {
	id: number;
	title: string;
	label: string;
	// Preloaded so the classroom options can carry the instructor, matching the
	// standalone-course and bundle dropdowns.
	instructor_id?: number;
	instructor?: DropdownProduct['instructor'];
	courses?: Array<Course>;
};

export type DropdownCourse = {
	id: number;
	name: string;
	code: string;
	units?: Array<CourseUnit>;
};
export type DropdownUnit = {
	id: number;
	course_id: number;
	name: string;
	url: string;
	type: string;
	active: boolean;
	progress_included?: boolean;
	created_at: string;
	updated_at: string;
	lms_id: string;
	type_id?: number;
	order?: number;
	add_to_sequence: boolean;
	current_course: boolean;
	unitType: {
		id: number;
		name: ContentType;
		icon: string;
	};
};

export type DropDownTestAttempts = {
	id: number;
	created_at: Date;
};

export type TestAttemptsPayload = {
	unitId: number | undefined;
	userId: number | undefined;
};

export type DropdownBatch = {
	batch: number;
	/** What the batch was generated for — admin endpoint only, so optional. */
	target_type?: string;
	target_label?: string | null;
	/** Batch value (instructor batches only; null = wildcard). */
	value?: number | null;
	total?: number;
	used?: number;
};
